import { useState } from "react";
import { useTranslation } from "react-i18next";
import "../../styles/mapStyles/HelpModal.css";

function HelpModal() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Open modal
  const openModal = () => setIsOpen(true);

  // Close modal
  const closeModal = () => setIsOpen(false);

  // Close modal when clicking outside (on backdrop)
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  return (
    <>
      {/* HELP BUTTON */}
      <button
        onClick={openModal}
        className="help-button"
        title={t("help.buttonTitle")}
        aria-label={t("help.buttonAriaLabel")}
      >
        <span className="help-icon">?</span>
      </button>

      {/* MODAL */}
      {isOpen && (
        <div className="help-modal-backdrop" onClick={handleBackdropClick}>
          <div
            className="help-modal-panel"
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER */}
            <div className="help-modal-header">
              <h1 className="help-modal-title">
                {t("help.title")}
              </h1>

              <button
                onClick={closeModal}
                className="help-modal-close"
                aria-label={t("help.closeModalAriaLabel")}
              >
                ✕
              </button>
            </div>

            {/* BODY */}
            <div className="help-modal-body">

              {/* SECTION 1: Getting Started */}
              <div className="help-section">
                <h2 className="help-section-title">
                  {t("help.gettingStarted")}
                </h2>

                <ol className="help-steps">
                  <li>
                    <strong>{t("help.allowLocationAccess")}</strong>

                    <p>
                      {t("help.allowLocationAccessText")}
                    </p>
                  </li>

                  <li>
                    <strong>{t("help.seeYourLocationPin")}</strong>

                    <p>
                      {t("help.seeYourLocationPinText")}
                    </p>
                  </li>

                  <li>
                    <strong>{t("help.clickThePin")}</strong>

                    <p>
                      {t("help.clickThePinText")}
                    </p>
                  </li>

                  <li>
                    <strong>{t("help.chooseLanguage")}</strong>

                    <p>
                      {t("help.chooseLanguageText")}
                    </p>
                  </li>
                </ol>
              </div>

              {/* SECTION 2: Getting Soil Information */}
              <div className="help-section">
                <h2 className="help-section-title">
                  {t("help.gettingSoilInformation")}
                </h2>

                <ol className="help-steps">
                  <li>
                    <strong>{t("help.clickGetSoilInfo")}</strong>

                    <p>
                      {t("help.clickGetSoilInfoText")}
                    </p>
                  </li>

                  <li>
                    <strong>{t("help.viewSoilValues")}</strong>

                    <p>
                      {t("help.viewSoilValuesText")}
                    </p>

                    <ul className="help-list">
                      <li>pH (soil acidity or alkalinity)</li>
                      <li>SOC (soil organic carbon)</li>
                      <li>Nitrogen (N)</li>
                      <li>Phosphorus (P)</li>
                      <li>Potassium (K)</li>
                    </ul>
                  </li>

                  <li>
                    <strong>
                      {t("help.readGeneralRecommendations")}
                    </strong>

                    <p>
                      {t("help.readGeneralRecommendationsText")}
                    </p>

                    <ul className="help-list">
                      <li>
                        <strong>
                          {t("help.phRecommendation")}
                        </strong>{" "}
                        {t("help.phRecommendationText")}
                      </li>

                      <li>
                        <strong>
                          {t("help.nitrogenRecommendation")}
                        </strong>{" "}
                        {t("help.nitrogenRecommendationText")}
                      </li>

                      <li>
                        <strong>
                          {t("help.phosphorusRecommendation")}
                        </strong>{" "}
                        {t("help.phosphorusRecommendationText")}
                      </li>

                      <li>
                        <strong>
                          {t("help.potassiumRecommendation")}
                        </strong>{" "}
                        {t("help.potassiumRecommendationText")}
                      </li>

                      <li>
                        <strong>
                          {t("help.socRecommendation")}
                        </strong>{" "}
                        {t("help.socRecommendationText")}
                      </li>
                    </ul>

                    <p
                      style={{
                        fontSize: "0.9rem",
                        color: "#6b7280",
                        marginTop: "0.5rem",
                      }}
                    >
                      💡 <em>{t("help.generalRecommendationTip")}</em>
                    </p>
                  </li>
                </ol>
              </div>

              {/* SECTION 3: Searching Locations */}
              <div className="help-section">
                <h2 className="help-section-title">
                  {t("help.searchingForDifferentLocations")}
                </h2>

                <p className="help-text">
                  {t("help.threeWaysToFindSoilData")}
                </p>

                <div className="help-subsection">
                  <h3>
                    {t("help.searchByLocationName")}
                  </h3>

                  <p>
                    {t("help.searchByLocationNameText")}
                  </p>
                </div>

                <div className="help-subsection">
                  <h3>
                    {t("help.searchByGpsCoordinates")}
                  </h3>

                  <p>
                    {t("help.searchByGpsCoordinatesText")}
                  </p>
                </div>

                <div className="help-subsection">
                  <h3>
                    {t("help.clickAndDrag")}
                  </h3>

                  <p>
                    {t("help.clickAndDragText")}
                  </p>
                </div>
              </div>

              {/* SECTION 4: Getting Fertilizer Recommendations */}
              <div className="help-section">
                <h2 className="help-section-title">
                  {t("help.gettingFertilizerRecommendations")}
                </h2>

                <p className="help-text">
                  {t("help.twoWaysToGetRecommendations")}
                </p>

                {/* Method 1: Automatic */}
                <div className="help-subsection">
                  <h3>
                    {t("help.method1Title")}
                  </h3>

                  <ol
                    className="help-steps"
                    style={{ marginTop: "0.5rem" }}
                  >
                    <li>
                      <strong>
                        {t("help.selectYourCrop")}
                      </strong>

                      <p>
                        {t("help.selectYourCropText")}
                      </p>
                    </li>

                    <li>
                      <strong>
                        {t("help.chooseFertilizerBlend")}
                      </strong>

                      <p>
                        {t("help.chooseFertilizerBlendText")}
                      </p>
                    </li>

                    <li>
                      <strong>
                        {t("help.clickGetRecommendation")}
                      </strong>

                      <p>
                        {t("help.clickGetRecommendationText")}
                      </p>
                    </li>
                  </ol>
                </div>

                {/* Method 2: Manual OFRA Calculator */}
                <div className="help-subsection">
                  <h3>
                    {t("help.method2Title")}
                  </h3>

                  <p>
                    {t("help.method2Text")}{" "}
                    <strong>{t("ofra.title")}</strong>
                  </p>

                  <p
                    style={{
                      fontSize: "0.9rem",
                      color: "#6b7280",
                      marginTop: "0.5rem",
                    }}
                  >
                    💡 <em>{t("help.ofraTip")}</em>
                  </p>
                </div>
              </div>

              {/* SECTION 5: Map Controls */}
              <div className="help-section">
                <h2 className="help-section-title">
                  {t("help.mapControls")}
                </h2>

                <div className="help-controls">
                  <div className="control-item">
                    <span className="control-icon">+</span>

                    <span className="control-desc">
                      {t("help.zoomIn")}
                    </span>
                  </div>

                  <div className="control-item">
                    <span className="control-icon">−</span>

                    <span className="control-desc">
                      {t("help.zoomOut")}
                    </span>
                  </div>
                </div>
              </div>

              {/* SECTION 6: Tips */}
              <div className="help-section help-tips">
                <h2 className="help-section-title">
                  💡 {t("help.proTips")}
                </h2>

                <ul className="help-tips-list">
                  <li>
                    {t("help.kadunaKanoOnly")}
                  </li>

                  <li>
                    {t("help.chromeMayTake30s")}
                  </li>

                  <li>
                    {t("help.worksOnDevices")}
                  </li>

                  <li>
                    {t("help.consultExtensionOfficer")}
                  </li>

                  <li>
                    {t("help.downloadPdfs")}
                  </li>
                </ul>
              </div>

              {/* SECTION 7: Troubleshooting */}
              <div className="help-section help-faq">
                <h2 className="help-section-title">
                  ❓ {t("help.commonQuestions")}
                </h2>

                <div className="faq-item">
                  <h3>
                    {t("help.whyLocationNotWork")}
                  </h3>

                  <p>
                    {t("help.whyLocationNotWorkText")}
                  </p>
                </div>

                <div className="faq-item">
                  <h3>
                    {t("help.noDataQuestion")}
                  </h3>

                  <p>
                    {t("help.noDataQuestionText")}
                  </p>
                </div>

                <div className="faq-item">
                  <h3>
                    {t("help.downloadPdfQuestion")}
                  </h3>

                  <p>
                    {t("help.downloadPdfQuestionText")}
                  </p>
                </div>

                <div className="faq-item">
                  <h3>
                    {t("help.automaticOrManualQuestion")}
                  </h3>

                  <p>
                    {t("help.automaticOrManualQuestionText")}
                  </p>
                </div>

                <div className="faq-item">
                  <h3>
                    {t("help.guaranteeQuestion")}
                  </h3>

                  <p>
                    {t("help.guaranteeQuestionText")}
                  </p>
                </div>

                <div className="faq-item">
                  <h3>
                    {t("help.printPdfQuestion")}
                  </h3>

                  <p>
                    {t("help.printPdfQuestionText")}
                  </p>
                </div>

                <div className="faq-item">
                  <h3>
                    {t("help.howOftenUpdatedQuestion")}
                  </h3>

                  <p>
                    {t("help.howOftenUpdatedQuestionText")}
                  </p>
                </div>
              </div>
            </div>

            {/* FOOTER */}
            <div className="help-modal-footer">
              <button
                onClick={closeModal}
                className="help-modal-button-close"
              >
                {t("help.closeGuide")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default HelpModal;
